import { BeneficiaryFilters } from '@repo/shared/dtos/beneficiary'
import { isEmptyString } from '@repo/shared/utils'
import { Button } from '@repo/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@repo/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Textarea } from '@repo/ui/textarea'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { PlusCircle, X } from 'lucide-react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { TaskDueDate } from '@/components/tasks/Task'
import { storeActions } from '@/store/actions'
import { selectCurrentFilters } from '@/store/beneficiary-list-filters/selectors'
import { useStoreDispatch, useStoreState } from '@/store/hooks'
import { Task } from '@/store/task'
import { createTasksForBeneficiariesMatchingFilters } from '@/store/tasks/thunks'
import { taskCtaSchema, taskSchema } from '@/validation-schemas'

export function TaskCreator() {
  const { tc } = useT()
  const [open, setOpen] = useState(false)
  const beneficiaryId = useStoreState((state) => state.beneficiary.currentBeneficiaryId)
  if (!beneficiaryId) return null
  return (
    <>
      <TooltipWrapper content={tc('createATask')}>
        <Button onClick={() => setOpen(true)} variant="outline">
          <PlusCircle className="size-4" /> {tc('create', { cfl: true })}
        </Button>
      </TooltipWrapper>
      {open && <TaskFormDialog beneficiaryId={beneficiaryId} modalHandler={{ open, setOpen }} />}
    </>
  )
}

export function TasksCreator({ disabled }: { disabled: boolean }) {
  const { t } = useT({ keyPrefix: 'taskCreator' })
  const [open, setOpen] = useState(false)
  const filters = useSelector(selectCurrentFilters)
  return (
    <>
      <Button disabled={disabled} onClick={() => setOpen(true)} variant="outline">
        <PlusCircle className="size-4" />
        {t('cta')}
      </Button>
      {open && <TaskFormDialog filters={filters} modalHandler={{ open, setOpen }} />}
    </>
  )
}

type TaskFormDialogProps = { modalHandler: { open: boolean; setOpen: (v: boolean) => void } }
export function TaskFormDialog({
  editableTask,
}: { editableTask: Task } & TaskFormDialogProps): ReactNode
export function TaskFormDialog({
  beneficiaryId,
}: { beneficiaryId: string } & TaskFormDialogProps): ReactNode
export function TaskFormDialog({
  filters,
}: { filters: BeneficiaryFilters } & TaskFormDialogProps): ReactNode
export function TaskFormDialog({ modalHandler, ...props }: TaskFormDialogProps) {
  const editableTask = 'editableTask' in props ? (props.editableTask as Task) : undefined
  const beneficiaryId = 'beneficiaryId' in props ? (props.beneficiaryId as string) : undefined
  const filters = 'filters' in props ? (props.filters as BeneficiaryFilters) : undefined
  const { t } = useT({ keyPrefix: editableTask ? 'taskEditor' : 'taskCreator' })
  return (
    <Dialog open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <DialogContent size="wide" disableOutsideClick>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        {editableTask ? (
          <TaskForm editableTask={editableTask} onSubmitted={() => modalHandler.setOpen(false)} />
        ) : beneficiaryId ? (
          <TaskForm beneficiaryId={beneficiaryId} onSubmitted={() => modalHandler.setOpen(false)} />
        ) : filters ? (
          <TaskForm filters={filters} onSubmitted={() => modalHandler.setOpen(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

type TaskFormProps = { onSubmitted: () => void }
function TaskForm({ editableTask }: { editableTask: Task } & TaskFormProps): ReactNode
function TaskForm({ beneficiaryId }: { beneficiaryId: string } & TaskFormProps): ReactNode
function TaskForm({ filters }: { filters: BeneficiaryFilters } & TaskFormProps): ReactNode
function TaskForm({ onSubmitted, ...props }: TaskFormProps) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const editableTask = 'editableTask' in props ? (props.editableTask as Task) : undefined
  const beneficiaryId = 'beneficiaryId' in props ? (props.beneficiaryId as string) : undefined
  const filters = 'filters' in props ? (props.filters as BeneficiaryFilters) : undefined

  const defaultValues = useMemo(
    () =>
      editableTask
        ? {
            categories: editableTask.categories,
            content: editableTask.content,
            cta: editableTask.cta,
            dueDate: editableTask.dueDate,
            title: editableTask.title,
          }
        : {
            categories: [],
            content: '',
            cta: undefined,
            dueDate: undefined,
            title: '',
          },
    [editableTask],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: (values) => {
      onSubmitted()

      let cta: Task['cta'] | undefined = undefined
      if (
        values.cta !== undefined &&
        taskCtaSchema.safeParse(values.cta).success &&
        values.cta.link !== undefined &&
        values.cta.link.trim() &&
        values.cta.name !== undefined &&
        values.cta.name.trim()
      ) {
        // @ts-expect-error checked
        cta = values.cta
      }

      const fields = {
        categories: values.categories && values.categories?.length > 0 ? values.categories : [],
        content: values.content,
        cta,
        dueDate: values.dueDate ?? undefined,
        title: values.title,
      }

      if (editableTask) {
        // Update existing task
        void dispatch(storeActions.task.update({ taskId: editableTask.id, fields }))
      } else if (beneficiaryId) {
        // Create new task
        void dispatch(storeActions.task.create({ fields }))
        form.reset(defaultValues)
      } else if (filters) {
        // Create tasks for beneficiaries matching filters
        void dispatch(
          createTasksForBeneficiariesMatchingFilters({
            filters,
            fields,
          }),
        )
        form.reset(defaultValues)
      }
    },
    schema: taskSchema.shape,
  })

  const [category, setCategory] = useState<string>('')
  const categories = form.watch().categories ?? []

  useEffect(() => {
    const { unsubscribe } = form.watch((values) => {
      if (
        values.cta &&
        typeof values.cta.name === 'string' &&
        isEmptyString(values.cta.name) &&
        values.cta &&
        typeof values.cta.link === 'string' &&
        isEmptyString(values.cta.link)
      ) {
        form.setValue('cta', undefined)
        form.clearErrors('cta')
        return
      }
    })
    return () => unsubscribe()
  }, [form.watch])

  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-2" onKeyDown={submitOnPressMetaEnter}>
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('title', { cfl: true })}</FormLabel>
              <FormControl>
                <Input placeholder={tc('title', { cfl: true })} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('content')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={tc('content')}
                  {...field}
                  className="h-60 resize-none text-base lg:h-80 xl:h-96"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Card>
          <CardHeader className="mb-0 pb-0">
            <CardDescription className="text-base">
              {tc('dueDate', { cfl: true, colon: true })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TaskDueDate
              isEditable
              initialDueDate={form.getValues('dueDate')}
              onDueDateChange={(date) => form.setValue('dueDate', date?.getTime())}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="mb-0 pb-0">
            <CardDescription className="text-base">
              {tc('cta', { cfl: true, colon: true })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="cta.name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('name')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={tc('name')} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cta.link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('url')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={tc('url')} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="mb-0 pb-2">
            <CardDescription className="text-base">
              {tc('category', {
                count: categories.length,
                cfl: true,
                colon: true,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <FormItem>
              <FormControl>
                <div className="flex space-x-2">
                  <Input
                    placeholder={tc('category', { cfl: true })}
                    value={category.toUpperCase()}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                  <Button
                    type="button"
                    disabled={
                      isEmptyString(category) ||
                      categories.includes(category) ||
                      category.length < 2
                    }
                    onClick={() => {
                      const v = category.trim().toUpperCase()
                      if (!isEmptyString(v) && !categories.includes(v)) {
                        form.setValue('categories', [...categories, v])
                      }
                      setCategory('')
                    }}
                  >
                    {tc('add', { cfl: true })}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>

            <div className="flex max-w-full flex-wrap gap-2">
              {categories.map((category, index) => (
                <div
                  key={index}
                  className="bg-primary-100 text-primary-600 flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-sm uppercase transition-colors hover:bg-red-500 hover:text-white"
                  onClick={() => {
                    form.setValue(
                      'categories',
                      categories.filter((_, i) => i !== index),
                    )
                  }}
                >
                  {category} <X className="size-4" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <DialogFooter>
          <Button type="submit" disabled={!isFormValid}>
            {tc(editableTask ? 'update' : 'create', { cfl: true })}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
